import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Divider, TextField, Button, Alert, Snackbar
} from '@mui/material';
import { ArrowBack, Refresh, Settings, Save } from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

const configDescriptions = {
  rate_limit_per_minute: { label: 'Rate limit general', unit: 'req/min', type: 'number' },
  rate_limit_auth_per_minute: { label: 'Rate limit auth', unit: 'req/min', type: 'number' },
  inactive_patient_days: { label: 'Paciente inactivo', unit: 'días', type: 'number' },
  stale_call_minutes: { label: 'Llamada colgada', unit: 'minutos', type: 'number' },
  jwt_expiry_hours: { label: 'Duración JWT', unit: 'horas', type: 'number' },
  max_login_attempts: { label: 'Intentos login max', unit: 'intentos', type: 'number' },
  lockout_duration_minutes: { label: 'Duración bloqueo', unit: 'minutos', type: 'number' },
};

export default function SystemConfig() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiRequest('/admin/config');
      setConfigs(data.config || []);
      const vals = {};
      (data.config || []).forEach(c => { vals[c.key] = c.value; });
      setEditValues(vals);
    } catch { setConfigs([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async (key) => {
    setSaving(key);
    try {
      await apiRequest(`/admin/config/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value: editValues[key] }),
      });
      setSnackbar({ open: true, message: 'Configuración guardada', severity: 'success' });
      fetchConfig();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Error al guardar', severity: 'error' });
    }
    setSaving('');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/admin/control-room')} size="small"><ArrowBack /></IconButton>
              <Typography variant="h5" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vincula</Typography>
              <Chip label="Configuración" size="small" variant="outlined" icon={<Settings sx={{ fontSize: 14 }} />} />
            </Stack>
            <Tooltip title="Refrescar"><IconButton onClick={fetchConfig}><Refresh /></IconButton></Tooltip>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          Estos valores se aplican al reiniciar los servicios. Los valores por defecto se usan si la configuración no existe en la base de datos.
        </Alert>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            configs.map((c, idx) => {
              const desc = configDescriptions[c.key] || { label: c.key, unit: '', type: 'text' };
              const hasChanged = editValues[c.key] !== c.value;
              return (
                <React.Fragment key={c.key}>
                  {idx > 0 && <Divider />}
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between"
                    sx={{ px: 3, py: 2 }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Typography variant="body1" fontWeight={600}>{desc.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.description || c.key}
                        {c.updated_name && ` · Editado por ${c.updated_name}`}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: { xs: 1, sm: 0 } }}>
                      <TextField
                        size="small"
                        type={desc.type}
                        value={editValues[c.key] || ''}
                        onChange={(e) => setEditValues({ ...editValues, [c.key]: e.target.value })}
                        sx={{ width: 120 }}
                        InputProps={{
                          endAdornment: desc.unit ? (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                              {desc.unit}
                            </Typography>
                          ) : null,
                        }}
                      />
                      {hasChanged && (
                        <Tooltip title="Guardar">
                          <IconButton size="small" onClick={() => handleSave(c.key)}
                            disabled={saving === c.key}
                            sx={{ color: '#2563EB' }}>
                            {saving === c.key ? <CircularProgress size={16} /> : <Save fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </React.Fragment>
              );
            })
          )}
        </Paper>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
